import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { UserAvatar, useGql } from '@ah/design';
import { BottomSheet } from '@ah/design/mobile';
import type { ChatUser, Conversation } from '../shared/types.ts';
import {
  CONNECTIONS_FOR_CHAT_QUERY,
  CREATE_OR_GET_CONVERSATION,
} from '../shared/queries.ts';
import { avatarProps, displayName } from './_parts.tsx';

interface ChatContact {
  id: string;
  user: ChatUser;
}

export function NewChatSheet({
  open,
  onClose,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (conversationId: string) => void;
}) {
  const gql = useGql();
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLoading(true);
    gql
      .request<{ chatContacts: ChatContact[] }>(CONNECTIONS_FOR_CHAT_QUERY)
      .then((d) => setContacts(d.chatContacts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, gql]);

  const filtered = contacts.filter((c) =>
    displayName(c.user).toLowerCase().includes(search.toLowerCase()),
  );

  const handlePick = async (u: ChatUser) => {
    setCreatingFor(u.id);
    try {
      const data = await gql.request<{ createOrGetConversation: Conversation }>(
        CREATE_OR_GET_CONVERSATION,
        { participantIds: [u.id] },
      );
      onStarted(data.createOrGetConversation.id);
    } catch {
      // noop
    } finally {
      setCreatingFor(null);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="New message">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-sm text-muted-foreground text-center">
          {search ? 'No matches' : 'No contacts yet'}
        </div>
      ) : (
        <ul>
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => handlePick(c.user)}
                disabled={creatingFor === c.user.id}
                className="w-full flex items-center gap-3 px-4 py-3 border-b border-border active:bg-muted text-left disabled:opacity-50"
              >
                <UserAvatar {...avatarProps(c.user)} className="size-10 shrink-0" />
                <span className="text-sm font-medium truncate">{displayName(c.user)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}
